import { AlertTriangle, ShieldCheck } from "lucide-react";
import { Link } from "@/lib/router-compat";

/**
 * Shared SPRAVATO® safety block — required on every SPRAVATO page.
 * Includes boxed warning, REMS certification, PI/Medication Guide links, and crisis footer.
 */
export const SpravatoSafetyBlock = () => (
  <div className="mt-10 rounded-2xl border border-border bg-card p-5 sm:p-8 shadow-soft space-y-5">
    {/* Boxed Warning */}
    <div className="flex items-center gap-3">
      <AlertTriangle className="h-6 w-6 text-accent shrink-0" />
      <h2 className="font-headings text-xl font-bold text-primary">
        Important Safety Information
      </h2>
    </div>

    <div className="border-l-4 border-accent pl-4 space-y-4">
      <p className="text-[15px] leading-relaxed text-foreground">
        <strong>BOXED WARNING:</strong> SPRAVATO® can cause sedation,
        dissociation, respiratory depression, abuse and misuse, and suicidal
        thoughts and behaviors. SPRAVATO® is only available through a restricted
        program called the SPRAVATO® REMS.
      </p>
      <p className="text-[15px] leading-relaxed text-foreground">
        SPRAVATO® can cause sleepiness, fainting, dizziness, spinning sensation,
        anxiety, dissociation, and breathing problems. Patients must be
        monitored by a healthcare provider for at least 2 hours after each dose.
        There is a risk of abuse and misuse, which may lead to physical and
        psychological dependence. Antidepressant medicines may increase suicidal
        thoughts and actions in some people 24 years of age and younger.
        SPRAVATO® is not for use in children.
      </p>
      <p className="text-[15px] leading-relaxed text-foreground">
        Do not take SPRAVATO® if you have aneurysmal vascular disease, an
        arteriovenous malformation, a history of bleeding in the brain, or an
        allergy to esketamine or ketamine.
      </p>
      <p className="text-[15px] leading-relaxed text-foreground">
        Do not drive or operate machinery until the next day after a restful
        sleep.
      </p>
      <p className="text-[15px] leading-relaxed text-foreground">
        The most common side effects include feeling disconnected from yourself,
        your thoughts, feelings and surroundings; dizziness; nausea; sleepiness;
        spinning sensation; numbness; anxiety; lack of energy; increased blood
        pressure; vomiting; feeling drunk; headache; and feeling very happy or
        excited. If these occur, they usually happen right after taking
        SPRAVATO® and go away the same day.
      </p>
    </div>

    {/* REMS Certification */}
    <div className="flex items-start gap-3 rounded-xl bg-light-blue/40 p-4">
      <ShieldCheck className="h-5 w-5 text-medium-blue shrink-0 mt-0.5" />
      <p className="text-[15px] leading-relaxed text-foreground">
        Neuro Wellness TMS Centers of America is a certified SPRAVATO® REMS
        treatment center. SPRAVATO® is only available at healthcare settings
        certified in the SPRAVATO® REMS program and must be self-administered
        under the supervision of a healthcare provider.
      </p>
    </div>

    {/* PI & Medication Guide links */}
    <p className="text-[15px] leading-relaxed text-muted-foreground">
      Please see the full{" "}
      <a
        href="https://www.janssenlabels.com/spravato"
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
      >
        Prescribing Information, including Boxed Warnings
      </a>
      , and the{" "}
      <a
        href="https://www.spravato.com/medication-guide"
        target="_blank"
        rel="noopener noreferrer"
        className="font-semibold text-primary underline underline-offset-2 hover:text-accent"
      >
        Medication Guide
      </a>
      , and discuss any questions with your healthcare provider.
    </p>

    {/* Trademark */}
    <p className="text-sm leading-relaxed text-muted-foreground">
      SPRAVATO® is a registered trademark of Johnson & Johnson and its
      affiliates. Neuro Wellness TMS Centers of America is not affiliated with
      or endorsed by Johnson & Johnson.
    </p>

    {/* Crisis */}
    <div className="rounded-xl border border-border bg-light-blue/30 p-4 text-center">
      <p className="text-sm sm:text-base font-medium text-primary">
        If you're in crisis, call or text{" "}
        <a
          href="tel:988"
          className="font-bold underline decoration-accent decoration-2 underline-offset-2"
        >
          988
        </a>{" "}
        (Suicide & Crisis Lifeline, 24/7) or call{" "}
        <a
          href="tel:911"
          className="font-bold underline decoration-accent decoration-2 underline-offset-2"
        >
          911
        </a>{" "}
        or go to your nearest emergency room.
      </p>
    </div>
  </div>
);

/**
 * Compact crisis banner for above-the-fold placement.
 */
export const SpravatoCrisisBanner = () => (
  <div className="rounded-xl border-l-4 border-accent bg-light-blue/40 px-5 py-4">
    <p className="text-sm sm:text-base font-medium text-primary">
      If you're in crisis or need someone to talk to right now, call or text{" "}
      <a
        href="tel:988"
        className="font-bold underline decoration-accent decoration-2 underline-offset-2"
      >
        988
      </a>{" "}
      any time — the Suicide and Crisis Lifeline is available 24/7. If you're in
      immediate danger, call{" "}
      <a
        href="tel:911"
        className="font-bold underline decoration-accent decoration-2 underline-offset-2"
      >
        911
      </a>
      .
    </p>
  </div>
);

/**
 * Internal links section for SPRAVATO scenario pages.
 */
export const SpravatoInternalLinks = () => (
  <div className="mt-8 flex flex-col gap-2">
    <Link
      to="/services/spravato"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → SPRAVATO® overview
    </Link>
    <Link
      to="/cost-insurance"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → Cost & insurance options
    </Link>
    <Link
      to="/spravato-what-to-expect"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → What to expect at your first session
    </Link>
    <Link
      to="/how-insurance-works"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → How insurance actually works here
    </Link>
    <Link
      to="/compare-treatments"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → Compare all treatments
    </Link>
    <Link
      to="/am-i-a-candidate"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → Am I a candidate?
    </Link>
    <Link
      to="/saint"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → SAINT® accelerated TMS
    </Link>
    <Link
      to="/ketamine-treatment-coral-springs-fl"
      className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
    >
      → IV ketamine infusions
    </Link>
  </div>
);
